import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Skeleton, Chip } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AppDispatch, RootState } from '../../store/store';
import { getInsights } from '../../rtk-apis/dashboard/getInsights';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Sonographer dashboard — acquisition-focused insights for the scanning tech.
   Distinct from the reader view: throughput, draft pipeline, handoff, peak days.
   MUI shell + KPI cards + Highcharts. Data from GET /v2/dashboard/insights
   (role-aware — returns the Sonographer payload).
   ════════════════════════════════════════════════════════════════════════ */

const fmtShortDate = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${+m[3]} ${mon}`;
};
const fmtStudyDate = (d?: string) => {
  if (!d) return '—';
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(String(d));
  if (!m) return String(d);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${+m[3]} ${mon} ${m[1]}`;
};

const chartBase: Highcharts.Options = {
  credits: { enabled: false },
  title: { text: undefined },
  chart: { backgroundColor: 'transparent', style: { fontFamily: "'Hanken Grotesk', sans-serif" } },
  legend: { itemStyle: { color: T.ink2, fontWeight: '600' }, itemHoverStyle: { color: T.ink } },
};

const SonographerDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { result, loading } = useSelector((s: RootState) => s.getInsightsResponse);
  const data = result?.data;

  useEffect(() => {
    dispatch(getInsights());
  }, [dispatch]);

  const k = data?.kpis;
  const statusB = data?.statusBreakdown || { Draft: 0, Preliminary: 0, Finalized: 0 };
  const trend = data?.trend || [];
  const modalityMix = data?.modalityMix || [];
  const weekdayVolume = data?.weekdayVolume || [];
  const draftQueue = data?.draftQueue || [];

  // ── charts ─────────────────────────────────────────────────────────────
  const trendOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'column', height: 280 },
    xAxis: { categories: trend.map((d: any) => fmtShortDate(d.date)), labels: { style: { color: T.ink3, fontSize: '10px' }, step: 4 }, lineColor: T.line, tickColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> studies' },
    plotOptions: { column: { borderRadius: 3, color: T.teal, borderWidth: 0 } },
    series: [{ type: 'column', name: 'Studies', data: trend.map((d: any) => d.studies) }],
  }), [trend]);

  const pipelineOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'pie', height: 280 },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
    plotOptions: { pie: { innerSize: '62%', borderWidth: 0, dataLabels: { enabled: true, style: { color: T.ink2, textOutline: 'none' }, format: '{point.name}: {point.y}' } } },
    series: [{
      type: 'pie', name: 'My reports',
      data: [
        { name: 'Draft (mine)', y: statusB.Draft, color: T.amber },
        { name: 'Awaiting sign-off', y: statusB.Preliminary, color: T.sky },
        { name: 'Finalized', y: statusB.Finalized, color: T.green },
      ],
    }],
  }), [statusB]);

  const modalityOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'bar', height: 280 },
    xAxis: { categories: modalityMix.map((m: any) => m.type), labels: { style: { color: T.ink2 } }, lineColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink } },
    plotOptions: { bar: { borderRadius: 4, colorByPoint: true, colors: [T.teal, T.sky, T.violet, T.amber, T.green, T.red] } },
    series: [{ type: 'bar', name: 'Studies', data: modalityMix.map((m: any) => m.count) }],
  }), [modalityMix]);

  const weekdayOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'column', height: 280 },
    xAxis: { categories: weekdayVolume.map((d: any) => d.day), labels: { style: { color: T.ink2 } }, lineColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> studies' },
    plotOptions: { column: { borderRadius: 3, color: T.sky, borderWidth: 0 } },
    series: [{ type: 'column', name: 'Studies', data: weekdayVolume.map((d: any) => d.count) }],
  }), [weekdayVolume]);

  return (
    <Box sx={{ px: '30px', pb: '30px', pt: '18px', color: T.ink, fontFamily: "'Hanken Grotesk', sans-serif", overflowY: 'auto', height: 'calc(100vh - 64px)' }}>
      {/* header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.teal, opacity: 0.85 }}>
          Acquisition Overview
        </Typography>
        <Typography component="h1" sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.05, background: 'linear-gradient(92deg,#fff,#bfe9f5 55%,#7fe3d4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Scanning Dashboard
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3,1fr)', xl: 'repeat(6,1fr)' }, gap: '14px', mb: '18px' }}>
        <Kpi loading={loading} icon={<GraphicEqOutlinedIcon />} accent={T.teal} label="Studies this month" value={k?.studiesThisMonth} />
        <Kpi loading={loading} icon={<TodayOutlinedIcon />} accent={T.sky} label="Scanned today" value={k?.studiesToday} />
        <Kpi loading={loading} icon={<EditNoteOutlinedIcon />} accent={T.amber} label="Drafts in progress" value={k?.draftsInProgress} highlight={(k?.draftsInProgress || 0) > 0} />
        <Kpi loading={loading} icon={<HourglassEmptyOutlinedIcon />} accent={T.violet} label="Awaiting sign-off" value={k?.awaitingSignoff} />
        <Kpi loading={loading} icon={<DonutLargeOutlinedIcon />} accent={T.green} label="Completion rate" value={k?.completionRate != null ? k.completionRate : '—'} unit={k?.completionRate != null ? '%' : ''} sub="finalized / scanned (90d)" />
        <Kpi loading={loading} icon={<GroupsOutlinedIcon />} accent={T.sky} label="Patients registered" value={k?.patientsRegistered} />
      </Box>

      {/* charts row 1 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="My scanning activity" subtitle="Studies acquired — last 30 days" loading={loading}>
          <HighchartsReact highcharts={Highcharts} options={trendOptions} />
        </Panel>
        <Panel title="My report pipeline" subtitle="Where your studies are now" loading={loading}>
          <HighchartsReact highcharts={Highcharts} options={pipelineOptions} />
        </Panel>
      </Box>

      {/* charts row 2 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="Exam type mix" subtitle="Your studies, last 90 days" loading={loading} empty={modalityMix.length === 0}>
          <HighchartsReact highcharts={Highcharts} options={modalityOptions} />
        </Panel>
        <Panel title="Busiest days" subtitle="Studies by weekday, last 90 days" loading={loading}>
          <HighchartsReact highcharts={Highcharts} options={weekdayOptions} />
        </Panel>
      </Box>

      {/* drafts to finish */}
      <Panel title="My drafts to finish" subtitle="Studies you started, not yet sent for reporting" loading={loading} empty={!loading && draftQueue.length === 0} emptyText="No open drafts — every study is handed off.">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {draftQueue.map((r: any) => (
            <Box
              key={r._id}
              onClick={() => navigate(`/patientReports/${r._id}`)}
              sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 140px 90px 24px', alignItems: 'center', gap: '12px', px: '12px', py: '11px', borderBottom: `1px solid ${T.line}`, cursor: 'pointer', borderRadius: '8px', '&:hover': { background: T.surface2 } }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reportName || r.uhid || 'Study'}</Typography>
                <Typography sx={{ fontSize: 12, color: T.ink3 }}>UHID {r.uhid || '—'}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: T.ink2 }}>{fmtStudyDate(r.studyDate)}</Typography>
              <Typography sx={{ fontSize: 13, color: T.ink3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reportType || '—'}</Typography>
              <Chip label="Draft" size="small" sx={{ fontWeight: 700, fontSize: 11, borderRadius: '999px', color: T.amber, background: `${T.amber}1f`, border: `1px solid ${T.amber}66` }} />
              <ChevronRightIcon sx={{ fontSize: 18, color: T.ink3 }} />
            </Box>
          ))}
        </Box>
      </Panel>
    </Box>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────
const Kpi: React.FC<{ icon: React.ReactNode; accent: string; label: string; value: any; unit?: string; sub?: string; highlight?: boolean; loading?: boolean }> = ({ icon, accent, label, value, unit, sub, highlight, loading }) => (
  <Box sx={{ minWidth: 0, position: 'relative', borderRadius: '14px', background: 'linear-gradient(180deg, rgba(20,36,57,0.6), rgba(14,28,46,0.55))', border: `1px solid ${highlight ? `${accent}88` : T.line}`, p: '14px 16px', overflow: 'hidden', minHeight: 96 }}>
    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: accent, mb: '6px' }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink3 }}>{label}</Typography>
      <Box sx={{ display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
    </Box>
    {loading ? (
      <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
    ) : (
      <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 30, fontWeight: 600, lineHeight: 1, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? 0}
        {unit && <Box component="span" sx={{ fontSize: 14, color: T.ink3, ml: '3px', fontFamily: "'Hanken Grotesk',sans-serif" }}>{unit}</Box>}
      </Typography>
    )}
    {sub && !loading && <Typography sx={{ fontSize: 11.5, color: T.ink3, mt: '4px' }}>{sub}</Typography>}
  </Box>
);

// ── chart / widget panel ───────────────────────────────────────────────────
const Panel: React.FC<{ title: string; subtitle?: string; loading?: boolean; empty?: boolean; emptyText?: string; children: React.ReactNode }> = ({ title, subtitle, loading, empty, emptyText, children }) => (
  <Box sx={{ minWidth: 0, overflow: 'hidden', borderRadius: '16px', background: 'rgba(14,28,46,0.55)', border: `1px solid ${T.line}`, p: '16px 18px' }}>
    <Box sx={{ mb: '8px' }}>
      <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 16, fontWeight: 600, color: T.ink }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12, color: T.ink3 }}>{subtitle}</Typography>}
    </Box>
    {loading ? (
      <Skeleton variant="rounded" height={240} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
    ) : empty ? (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink3, fontSize: 13 }}>{emptyText || 'No data yet.'}</Box>
    ) : (
      children
    )}
  </Box>
);

export default SonographerDashboard;
